"use client";

import type { FestivalDetails, ProcessedSet, Review } from "@workspace/types";
import { type CSSProperties, lazy, memo, Suspense, useMemo } from "react";
import { CalendarTable } from "@/components/features/festival/calendar/calendar-table";
import { HorizontalCalendarTable } from "@/components/features/festival/calendar/horizontal-calendar-table";
import { RealtimeCalendarSetCard } from "@/components/features/festival/calendar/realtime-calendar-set";
import { CalendarSkeleton } from "@/components/features/festival/skeletons/calendar-skeleton";
import { CalendarEmptyState } from "@/components/features/festival/states";
import type { TabVariant } from "./use-tab-variant";

// Dynamic imports for performance
const LazyCalendarTable = lazy(() =>
	import("@/components/features/festival/calendar/calendar-table").then(
		(m) => ({
			default: m.CalendarTable,
		}),
	),
);
const LazyHorizontalCalendarTable = lazy(() =>
	import(
		"@/components/features/festival/calendar/horizontal-calendar-table"
	).then((m) => ({
		default: m.HorizontalCalendarTable,
	})),
);

// Unified calendar data interface
interface CalendarData {
	id: string;
	artist: string;
	stage: string;
	time: string;
	startHour: number;
	duration: number;
	imageUrl?: string | null;
	youtube_url?: string | null;
	status?: {
		isInSchedule: boolean;
		sawLive: boolean;
		sawStream: boolean;
		mustSee: boolean;
	};
}

interface CalendarTabProps {
	// Core data
	calendarData: CalendarData[];
	unfilteredCalendarData: Array<{ stage: string }>;
	userInteractionCalendarData: Array<{ stage: string }>;
	userIntentCalendarData?: Array<{ stage: string }>;
	processedSets: ProcessedSet[];
	festivalDetails: FestivalDetails | null;
	dbStagesList: { id: string; name: string }[];
	festivalSlug: string;

	// Variant configuration
	variant: TabVariant;

	// Authentication/ownership
	authState: boolean; // isOwnProfile for personal, isAuthenticated for public

	// Reviews data - single source of truth pattern
	reviews?: Review[]; // Unified review format from parent

	// Schedule data
	savedSchedule: string[];

	// Display configuration
	isHorizontalLayout: boolean;
	sortAscending: boolean;
	calendarDimensions: {
		hourWidth: number;
		minSetWidth: number;
		hourHeight: number;
		minSetHeight: number;
	};

	// Calendar preferences
	calendarPreferences?: import("@workspace/types/src/entities/user").CalendarViewPreferences;

	// Filters
	sharedBookmarkFilters?: {
		saved: boolean;
		rated: boolean;
		following: boolean;
		stream: boolean;
	} | null;

	// Event handlers
	onStartReview: (setId: string) => void;
	onToggleSetInSchedule: (setId: string) => void;
	onResetCalendarPreferences?: () => void;
	onClearFilters?: () => void;
	onAuthRequired?: () => void;

	// Current time indicator
	selectedDay?: string; // Current selected day name (e.g., "friday")
}

export const CalendarTab = memo(function CalendarTab({
	calendarData,
	unfilteredCalendarData,
	userInteractionCalendarData,
	processedSets,
	festivalDetails,
	dbStagesList,
	festivalSlug,
	variant,
	authState,
	reviews,
	savedSchedule,
	isHorizontalLayout,
	sortAscending,
	calendarDimensions,
	calendarPreferences,
	sharedBookmarkFilters,
	onStartReview,
	onToggleSetInSchedule,
	onResetCalendarPreferences,
	onClearFilters,
	onAuthRequired,
	selectedDay,
}: CalendarTabProps) {
	// Determine which components to use based on variant
	const useLazyComponents = variant === "public";
	const CalendarTableComponent = useLazyComponents
		? LazyCalendarTable
		: CalendarTable;
	const HorizontalCalendarTableComponent = useLazyComponents
		? LazyHorizontalCalendarTable
		: HorizontalCalendarTable;
	// Always use RealtimeCalendarSetCard for real-time updates
	const CalendarSetCardComponent = RealtimeCalendarSetCard;

	// Render function for calendar set cards
	const renderCalendarSetCard = (
		set: CalendarData,
		_statusInfo: { icon: string; text: string },
		style: CSSProperties,
		isHorizontal: boolean,
	) => {
		const dbSet = processedSets.find((s) => s.id === set.id);
		if (!dbSet) return null;

		// Find the review for this set from batched parent data
		// null = parent checked, no review exists (keeps per-set hook disabled)
		// undefined = parent didn't batch (falls back to per-set hook)
		const review = reviews
			? (reviews.find((r) => r.setId === set.id) ?? null)
			: undefined;


		return (
			<div
				key={set.id}
				className={
					isHorizontal
						? "absolute mx-0.5 h-[calc(100%-4px)]"
						: "absolute right-0 left-0 mx-0.5"
				}
				style={
					isHorizontal
						? {
								...style,
								top: "2px",
								left: !sortAscending
									? `calc(${style.left} - 34px)`
									: `calc(${style.left} - 2px)`,
							}
						: {
								...style,
								top: `calc(${style.top} + 2px)`,
							}
				}
			>
				<CalendarSetCardComponent
					set={dbSet}
					review={review}
					savedSchedule={savedSchedule}
					festivalSlug={festivalSlug}
					variant={variant}
					onStartReview={onStartReview}
					onToggleSetInSchedule={onToggleSetInSchedule}
					topOffset="0"
					setHeight="100%"
					isHorizontal={isHorizontal}
					readOnly={!authState}
					onAuthRequired={onAuthRequired}
				/>
			</div>
		);
	};

	// Get only stages that have sets for this festival
	// Use different data sources based on user preference
	const activeStageNames = useMemo(() => {
		// Determine which data source to use based on calendar preferences
		// show_stage_names: true = show filtered stages only (with user interactions)
		// show_stage_names: false = show all stages
		const shouldShowFilteredStages =
			calendarPreferences?.show_stage_names ?? false;
		// Choose the appropriate data source
		const dataToUse = shouldShowFilteredStages
			? userInteractionCalendarData // Only stages with saved/rated sets
			: unfilteredCalendarData; // All stages

		const stagesWithSets = new Set<string>();
		for (const data of dataToUse) {
			if (data.stage) {
				stagesWithSets.add(data.stage);
			}
		}

		// Sort by original stage order
		return dbStagesList
			.filter((stage) => stagesWithSets.has(stage.name))
			.map((stage) => stage.name);
	}, [
		unfilteredCalendarData,
		userInteractionCalendarData,
		dbStagesList,
		calendarPreferences,
	]);

	// Check if we should show the empty state
	const showEmptyState = useMemo(() => {
		return false; // Simplified for now
	}, []);

	// Check if filters are active
	const hasActiveFilters = useMemo(() => {
		return (
			sharedBookmarkFilters &&
			(sharedBookmarkFilters.saved ||
				sharedBookmarkFilters.rated ||
				sharedBookmarkFilters.following ||
				sharedBookmarkFilters.stream)
		);
	}, [sharedBookmarkFilters]);

	// Show empty state for no stages or no sets matching filters
	if (showEmptyState && onResetCalendarPreferences) {
		return <CalendarEmptyState onShowAllStages={onResetCalendarPreferences} />;
	}

	// Show empty state when calendar has stages but no sets match the filters
	if (
		activeStageNames.length > 0 &&
		calendarData.length === 0 &&
		hasActiveFilters
	) {
		return (
			<CalendarEmptyState
				onShowAllStages={() => {
					// This will be handled by parent component to clear filters
					// For now, we just show the empty state with the button
				}}
				onClearFilters={onClearFilters}
				hasActiveFilters={true}
			/>
		);
	}

	return (
		<div>
			<Suspense
				fallback={<CalendarSkeleton isHorizontal={isHorizontalLayout} />}
			>
				{isHorizontalLayout ? (
					<HorizontalCalendarTableComponent
						data={calendarData}
						allStageNames={activeStageNames}
						className="w-full"
						readOnly={!authState}
						inverseOrder={!sortAscending}
						onToggleSet={onToggleSetInSchedule}
						onStartReview={onStartReview}
						renderItem={(set, statusInfo, style) =>
							renderCalendarSetCard(set, statusInfo, style, true)
						}
						scheduleStartHour={festivalDetails?.schedule_start_hour ?? 12}
						scheduleEndHour={festivalDetails?.schedule_end_hour ?? 0}
						scheduleIsOvernight={festivalDetails?.schedule_overnight ?? false}
						hourWidth={calendarDimensions?.hourWidth}
						minSetWidth={calendarDimensions?.minSetWidth}
						selectedDay={selectedDay}
						festivalDetails={festivalDetails}
					/>
				) : (
					<CalendarTableComponent
						data={calendarData}
						allStageNames={activeStageNames}
						className="w-full"
						readOnly={!authState}
						inverseOrder={!sortAscending}
						onToggleSet={onToggleSetInSchedule}
						onStartReview={onStartReview}
						renderItem={(set, statusInfo, style) =>
							renderCalendarSetCard(set, statusInfo, style, false)
						}
						scheduleStartHour={festivalDetails?.schedule_start_hour ?? 12}
						scheduleEndHour={festivalDetails?.schedule_end_hour ?? 0}
						scheduleIsOvernight={festivalDetails?.schedule_overnight ?? false}
						hourHeight={calendarDimensions?.hourHeight}
						minSetHeight={calendarDimensions?.minSetHeight}
						selectedDay={selectedDay}
						festivalDetails={festivalDetails}
					/>
				)}
			</Suspense>
		</div>
	);
});
